import type { SeasonThreshold } from '../../api/seasons';
import { TIER_COLORS } from './SeasonStrip';

/**
 * Bronze → Silver → Gold, marking which the viewer has already crossed.
 *
 * Thresholds are never hardcoded here — they come straight from the API
 * (`CurrentSeasonView.thresholds`), which is deliberate: the real numbers
 * are provisional and will be recalibrated against production data before
 * launch, so this component must render whatever the server sends.
 */
export function TierLadder({
  thresholds,
  points,
}: {
  /** Ascending by `min`, as the API guarantees. */
  thresholds: SeasonThreshold[];
  /** The viewer's season points. 0 for a signed-out visitor or a
   *  signed-in player who hasn't played this season — both read as
   *  "nothing reached yet", not an error state. */
  points: number;
}) {
  return (
    <ol
      data-testid="tier-ladder"
      className="divide-y divide-tile-edge/60 rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
    >
      {thresholds.map((t) => {
        const reached = points >= t.min;
        return (
          <li
            key={t.name}
            data-testid={`tier-row-${t.name}`}
            className="flex items-center gap-3 px-4 py-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-4 w-4 shrink-0 rounded-full"
              style={{ backgroundColor: TIER_COLORS[t.name] }}
            />
            <span
              className={`flex-1 text-sm font-bold ${reached ? 'text-stone-900' : 'text-stone-500'}`}
            >
              {t.name}
            </span>
            <span className="text-sm tabular-nums text-stone-600">
              {t.min.toLocaleString('en-GB')} pts
            </span>
            {reached ? (
              <span
                data-testid={`tier-check-${t.name}`}
                aria-label="Reached"
                className="ml-1 text-sm font-bold text-emerald-700"
              >
                ✓
              </span>
            ) : (
              // Reserves the same width as the check mark so rows don't
              // shift between reached/unreached states.
              <span aria-hidden="true" className="ml-1 w-[1ch] text-sm">
                &nbsp;
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
